import {Component, ElementRef, QueryList, ViewChildren} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ACCOUNT_LOGIN_ROUTE,
  DEFAULT_ROUTE_CONSTANT,
  DESTINATION_KEY_CONSTANT,
  EMAIL_VERIFICATION_COMPLETE_ROUTE,
  EM,
  LOGIN_OBJECT_KEY_CONSTANT,
  PRODUCT_KEY_CONSTANT,
} from '../constants/routes.constant';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormArray, FormBuilder, FormControl, Validators,} from '@angular/forms';
import {mergeMap, Subject} from 'rxjs';
import {PROGRESS_BAR_SUBJECT} from '../app.component';
import {CreateAccountComponent} from '../create-account/create-account.component';
import { HttpStatusCode } from "@angular/common/http";
import {UserProfileService} from "../services/user-profile.service";

@Component({
    selector: 'app-verify-email',
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.scss'],
    standalone: false
})
export class VerifyEmailComponent {
  public inProgress: boolean = false;
  private confirmationRoute: string = EMAIL_VERIFICATION_COMPLETE_ROUTE;
  public form!: FormArray;
  public MAX_CONTROLS_LENGTH: number = 6;
  public email!: string;
  @ViewChildren('otpBoxes', { read: ElementRef<HTMLInputElement> })
  public otpBoxes!: QueryList<ElementRef<HTMLInputElement>>;
  private productNameKey: string = PRODUCT_KEY_CONSTANT;
  private destinationKey: string = DESTINATION_KEY_CONSTANT;
  private loginObjectKey: string = LOGIN_OBJECT_KEY_CONSTANT;
  private productName!: string;
  private destination!: string;
  private loginObject!: string;
  public queryParams: { [key: string]: string } = {
    [this.productNameKey]: this.productName,
    [this.destinationKey]: this.destination,
    [this.loginObjectKey]: this.loginObject,
  };
  public subject: Subject<boolean> = PROGRESS_BAR_SUBJECT;
  private loginRoute: string = ACCOUNT_LOGIN_ROUTE;
  private em: string = EM;

  constructor(
    private readonly router: Router,
    private snackBar: MatSnackBar,
    private readonly formBuilder: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userProfileService: UserProfileService,
  ) {}

  ngOnInit() {
    this.initializeForm();

    this.email = atob(this.activatedRoute.snapshot.paramMap.get(
      this.em
    )!);
    this.productName = this.activatedRoute.snapshot.queryParamMap.get(
      this.productNameKey
    )!;
    this.destination = this.activatedRoute.snapshot.queryParamMap.get(
      this.destinationKey
    )!;
    this.loginObject = this.activatedRoute.snapshot.queryParamMap.get(
      this.loginObjectKey
    )!;

    if (!this.productName || !this.destination) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
        queryParamsHandling: 'preserve',
      });
    }

    this.queryParams = {
      [this.productNameKey]: this.productName,
      [this.destinationKey]: this.destination,
      [this.loginObjectKey]: this.loginObject,
    };

    this.initializeListeners();
  }

  public resendEmailVerification() {
    this.subject.next(true);
    this.inProgress = true;
    const email = this.email;

    if (!email) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
        queryParamsHandling: 'preserve',
      });
      return;
    }

    this.userProfileService
      .sendEmailVerification(email, this.productName, this.destination)
      .pipe(
        mergeMap(() => {
          // INFO Route to verify email page
          return this.router.navigate(['/', ...this.loginRoute.split('/')], {
            queryParams: this.queryParams,
            queryParamsHandling: 'preserve',
          });
        })
      )
      .subscribe({
        next: () => {
          this.inProgress = false;
          this.snackBar.open('Verification resent!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-primary',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
        },
        error: () => {
          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open('An error occurred!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
        },
      });
  }

  public completeEmailVerification() {
    const code = this.form.getRawValue().join('');

    this.subject.next(true);
    this.inProgress = true;

    // INFO Seems to work, but I am not certain, I expected auth instance to be a logged auth instance
    this.userProfileService
      .completeEmailVerification(this.email, code)
      .subscribe({
        next: () => {
          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open('Verification complete!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-primary',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });

          CreateAccountComponent.removeOnboardingToken();

          this.router.navigate(['/', ...this.confirmationRoute.split('/')], {
            queryParams: this.queryParams,
          });
        },
        error: (error) => {
          const errorMessage = error.status === HttpStatusCode.Conflict ?
            'Account already exist. Kindly contact support if you have registered prior.' :
            error.code?.toLowerCase() === 'auth/invalid-action-code' || error.code === HttpStatusCode.BadRequest ?
              'Code has expired or is invalid. Please hit the "Resend Code" button.' :
              'An error occurred!';

          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open(errorMessage, 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });

          this.router.navigate(['/', ...this.loginRoute.split('/')], {
            queryParams: this.queryParams,
            queryParamsHandling: 'preserve',
          });
        },
      });
  }

  public getControlAt<T = FormControl>(index: number) {
    return this.form.at(index) as T;
  }

  public onKeyUp(index: number, e: KeyboardEvent) {
    if (index >= 0 && e.code.toLowerCase() === 'backspace') {
      this.otpBoxes.get(index - 1)?.nativeElement.focus();
    } else {
      this.otpBoxes.get(index + 1)?.nativeElement.focus();
    }
  }

  public initializeListeners() {
    for (let i = 0; i < this.MAX_CONTROLS_LENGTH; i++) {
      const control = this.form.at(i);

      control.valueChanges.subscribe({
        next: () => {
          const value: string = control.value;

          if (!value || value.length < this.MAX_CONTROLS_LENGTH) return;

          this.form.setValue(
            value.split('').slice(0, this.MAX_CONTROLS_LENGTH),
            { emitEvent: false }
          );
        },
      });
    }
  }

  public initializeForm() {
    this.form = this.formBuilder.array(
      Array.from({ length: this.MAX_CONTROLS_LENGTH }).map(
        () =>
          new FormControl(null, [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(1),
          ])
      )
    );
  }
}
